import { useEffect, useRef, type RefObject } from "react";
import { getFocusableElements, isFocusableElement } from "../../utils/focus-trap";
import { findRenderedAnchorElement, inertSiblings } from "./tourDom";

export interface TourModalityOptions {
  /**
   * The step expects the coach to work the real page (an interactive step). While one is active
   * nothing is made inert, which is what lets a real click reach the control and keeps a popup
   * that opens mid-step alive rather than dead the moment it mounts.
   */
  interactive: boolean;
  /**
   * The anchor the coach has to activate, on a `ClickToContinue` step. It joins the bubble's Tab
   * ring, ahead of the bubble's own buttons, so the control the step asks for is one Tab away and
   * a keyboard user can reach it at all — the ring is the only way out of the trap.
   */
  interactiveAnchorId: string | null;
}

/**
 * What Tab cycles through: the control the step asks for, if any, then the bubble's own.
 *
 * The anchor has to **render** to join the ring, not merely match the focusable selector. An
 * element with no box cannot take focus, so a `display: none` control carrying the anchor would sit
 * at index 0 as a dead stop, stranding a keyboard user before Skip and Next.
 */
const focusRing = (bubble: HTMLElement, anchorId: string | null): HTMLElement[] => {
  const anchor = anchorId ? findRenderedAnchorElement(anchorId) : null;
  const bubbleFocusable = getFocusableElements(bubble);
  return anchor && isFocusableElement(anchor) ? [anchor, ...bubbleFocusable] : bubbleFocusable;
};

const moveFocus = (bubble: HTMLElement, anchorId: string | null, event: KeyboardEvent) => {
  const ring = focusRing(bubble, anchorId);
  if (ring.length === 0) {
    bubble.focus();
    return;
  }
  const index = ring.indexOf(document.activeElement as HTMLElement);
  // Focus outside the ring is where every step starts: the title takes focus to announce the step
  // and is not tabbable. Stepping from -1 would find nothing going backwards, and this press is
  // prevented regardless, so it wraps to the near edge instead of being swallowed.
  const target =
    index === -1
      ? ring[event.shiftKey ? ring.length - 1 : 0]
      : ring[(index + (event.shiftKey ? -1 : 1) + ring.length) % ring.length];
  const before = document.activeElement;
  target?.focus();
  // `focus()` is a no-op on an element that cannot take it, and this press is prevented, so the
  // Tab would be swallowed and the coach stuck wherever they were. Fall back into the bubble,
  // which always has Skip.
  if (document.activeElement === before && before !== target) {
    (getFocusableElements(bubble)[0] ?? bubble).focus();
  }
};

/**
 * Focus has left the tour: neither in the bubble nor on the control the step asks for.
 *
 * Only asked on an interactive step, where the page is live and a popup the coach just opened is
 * theirs to work. Tab inside that popup belongs to the popup, not to the tour's ring.
 */
const focusIsOutsideTheTour = (bubble: HTMLElement, anchorId: string | null): boolean => {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body) return false;
  if (bubble.contains(active)) return false;
  const anchor = anchorId ? findRenderedAnchorElement(anchorId) : null;
  return !(anchor !== null && anchor.contains(active));
};

const focusPageHeading = () => {
  const heading = document.querySelector<HTMLElement>("main h1, h1");
  if (!heading) return;
  if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
  heading.focus();
};

/**
 * Makes the tour overlay the only interactive thing on the page while it is open.
 *
 * - `inert` on every other child of `body`, restored exactly on close. Lifted for the length of an
 *   interactive step, because `inert` is inherited and cannot be undone on a descendant: the only
 *   way to let a click reach one control inside the app root is to stop inerting the root.
 * - Tab and Escape on a **window capture** listener that stops propagation. `useDialogFocus`
 *   listens on `document` capture, which window capture runs before, so a host dialog under a
 *   flow-step tour never cycles focus out of the bubble or closes itself on the tour's Escape. The
 *   one exception, for both keys, is an interactive step once focus has left both the bubble and
 *   the step's control: the page is live for that step, and those keys belong to whatever the
 *   coach opened.
 * - Focus returns on close to what had it before, or to the page heading if that is gone.
 */
export const useTourModality = (
  rootRef: RefObject<HTMLElement | null>,
  bubbleRef: RefObject<HTMLElement | null>,
  onEscape: () => void,
  { interactive, interactiveAnchorId }: TourModalityOptions = {
    interactive: false,
    interactiveAnchorId: null,
  }
) => {
  const onEscapeRef = useRef(onEscape);
  const anchorIdRef = useRef(interactiveAnchorId);
  const interactiveRef = useRef(interactive);
  useEffect(() => {
    onEscapeRef.current = onEscape;
    anchorIdRef.current = interactiveAnchorId;
    interactiveRef.current = interactive;
  }, [onEscape, interactiveAnchorId, interactive]);

  // Declared before the focus effect on purpose: cleanups run in that order, and an inert element
  // cannot take focus, so inert has to be restored before focus is handed back on close.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || interactive) return;
    return inertSiblings(root);
  }, [rootRef, interactive]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const previouslyFocused = document.activeElement;

    const handleKeyDown = (event: KeyboardEvent) => {
      const bubble = bubbleRef.current;
      if (!bubble) return;
      if (event.key === "Escape") {
        // The same hand-back Tab gets, and for a sharper reason. Escape inside a popup the coach
        // was asked to open means "close this popup"; taken by the tour it means Skip, which
        // records the whole run as seen and never auto-shows it again — while the popup stays
        // open. Let the page have the key; the step falls back to read-and-Next once its control
        // reports shut.
        if (interactiveRef.current && focusIsOutsideTheTour(bubble, anchorIdRef.current)) return;
        event.preventDefault();
        event.stopPropagation();
        onEscapeRef.current();
      } else if (event.key === "Tab") {
        // An interactive step hands the page back for the length of the step, and once focus has
        // left both the bubble and the control the step names, Tab is the coach's — moving between
        // the fields of the popup they were just asked to open. Pulling it back into the tour's
        // ring makes that popup unusable from a keyboard. Focus inside the bubble still cycles the
        // ring, so Skip and Back are always one Tab away.
        if (interactiveRef.current && focusIsOutsideTheTour(bubble, anchorIdRef.current)) return;
        moveFocus(bubble, anchorIdRef.current, event);
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      if (
        previouslyFocused instanceof HTMLElement &&
        previouslyFocused.isConnected &&
        previouslyFocused !== document.body &&
        !root.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      } else {
        focusPageHeading();
      }
    };
  }, [rootRef, bubbleRef]);
};
